import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { AppId } from "@/lib/apps";
import { logBehavioralSignal } from "@/lib/safety/useBehavioralSignals";
import {
  applyClientFilters,
  DEFAULT_FILTERS,
  filtersDelta,
  loadFilters,
  saveFilters,
  type DiscoveryFilters,
} from "./filters";
import type {
  DiscoveryCandidate,
  LikeAction,
  RegisterActionResult,
} from "./types";

interface Options {
  app: AppId | null;
  limit?: number;
}

export const useDiscovery = ({ app, limit = 25 }: Options) => {
  const [candidates, setCandidates] = useState<DiscoveryCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFiltersState] = useState<DiscoveryFilters>(loadFilters);

  // Rolling outbound action timestamps for velocity-based behavioral signals.
  const recentActions = useRef<number[]>([]);
  const recentMatches = useRef<number[]>([]);

  const load = useCallback(async () => {
    if (!app) return;
    setLoading(true);
    setError(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase as any).rpc("fn_discover_candidates", {
      _app: app,
      _max_km: filters.maxKm,
      _limit: limit,
    });
    if (error) setError(error.message);
    setCandidates((data as DiscoveryCandidate[]) ?? []);
    setLoading(false);
  }, [app, filters.maxKm, limit]);

  useEffect(() => {
    void load();
  }, [load]);

  const setFilters = useCallback((next: DiscoveryFilters) => {
    setFiltersState(next);
    saveFilters(next);
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
    saveFilters(DEFAULT_FILTERS);
  }, []);

  const filtered = useMemo(
    () => applyClientFilters(candidates, filters),
    [candidates, filters],
  );

  const filterDelta = useMemo(() => filtersDelta(filters), [filters]);

  const act = useCallback(
    async (
      targetId: string,
      action: LikeAction,
      voiceNoteUrl?: string,
    ): Promise<RegisterActionResult | { error: string }> => {
      if (!app) return { error: "no_app" };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("fn_register_action", {
        _target: targetId,
        _app: app,
        _action: action,
        _voice_note_url: voiceNoteUrl ?? null,
      });
      if (error) return { error: error.message };
      // remove from local stack
      setCandidates((prev) => prev.filter((c) => c.id !== targetId));

      // Behavioral risk telemetry — fire-and-forget. Never blocks discovery.
      const now = Date.now();
      recentActions.current = [...recentActions.current.filter((t) => now - t < 3_600_000), now];
      const result = data as RegisterActionResult;
      const isMatch = Boolean(result?.matched);
      if (isMatch) {
        recentMatches.current = [
          ...recentMatches.current.filter((t) => now - t < 3_600_000),
          now,
        ];
      }

      // 1. Mass-swipe / spam pattern: >60 actions in the last hour.
      if (recentActions.current.length >= 60) {
        logBehavioralSignal({
          kind: "rapid_messaging",
          weight: 0.8,
          evidence: {
            window: "1h",
            action_count: recentActions.current.length,
            surface: "discovery",
          },
        });
      }

      // 2. Match-burst: 10+ new matches in the last hour suggests bot/farm
      //    behavior. Logged at match-create per spec.
      if (isMatch && recentMatches.current.length >= 10) {
        logBehavioralSignal({
          kind: "duplicate_account_signal",
          weight: 1.0,
          evidence: {
            window: "1h",
            match_burst: recentMatches.current.length,
            target_user_id: targetId,
            app,
          },
        });
      }

      return result;
    },
    [app],
  );

  return {
    candidates: filtered,
    rawCandidates: candidates,
    loading,
    error,
    reload: load,
    act,
    filters,
    setFilters,
    resetFilters,
    filterDelta,
  };
};
